import React from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
} from "react-native";

export type DateRangePickerProps = {
  visible: boolean;
  onClose: () => void;
  options: Array<TDateRangeOption>;
  onSelect: (option: TDateRangeOption) => void;
  activeOffset: number;
  viewMode: "daily" | "weekly";
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  visible,
  onClose,
  options,
  onSelect,
  activeOffset,
  viewMode,
}) => {
  const renderItem = ({ item }: { item: TDateRangeOption }) => {
    const isSelected = item.offset === activeOffset;

    return (
      <TouchableOpacity
        style={[styles.option, isSelected && styles.optionSelected]}
        onPress={() => {
          onSelect(item);
          onClose();
        }}
      >
        <View>
          <Text style={[styles.optionLabel, isSelected && styles.selectedText]}>
            {item.viewLabel}
          </Text>
          <Text style={styles.optionSubLabel}>
            {item.startDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            {viewMode === "weekly" &&
              ` - ${item.endDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}`}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      {visible && (
        <View style={styles.overlay}>
          <View style={styles.sheet}>
            <View style={styles.header}>
              <Text style={styles.title}>
                {viewMode === "weekly" ? "Select Week" : "Select Day"}
              </Text>
              <TouchableOpacity onPress={onClose}>
                <Text style={styles.closeText}>Close</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={options}
              renderItem={renderItem}
              keyExtractor={(item) =>
                `${item.startDate.toISOString()}-${item.offset}`
              }
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />
          </View>
        </View>
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.3)",
    justifyContent: "center",
    padding: 16,
  },
  sheet: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    maxHeight: "80%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
  },
  closeText: {
    color: "#0085FF",
    fontWeight: "500",
  },
  separator: {
    height: 1,
    backgroundColor: "#E5E7EB",
  },
  option: {
    paddingVertical: 12,
  },
  optionSelected: {
    backgroundColor: "#F0F9FF",
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#111827",
  },
  optionSubLabel: {
    fontSize: 13,
    color: "#6B7280",
  },
  selectedText: {
    color: "#0085FF",
  },
});

export type TDateRangeOption = {
  startDate: Date;
  endDate: Date;
  viewLabel: string;
  isNextWeek: boolean;
  offset: number;
};

export default DateRangePicker;
